import React, { useEffect, useState } from 'react';
import { X, Loader2, StickyNote, Trash2, Clock } from 'lucide-react';
import api from '@/utils/api';
import { showError, showSuccess } from '@/utils/toast';
import { useAuth } from '@/context/AuthContext';

const PRIORITY_STYLES = {
  low: 'bg-blue-100 text-blue-700',
  medium: 'bg-amber-100 text-amber-700',
  high: 'bg-rose-100 text-rose-700',
};

const formatRole = (r) => (r || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const formatTime = (iso) => (iso ? new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

const NotesPanel = ({ leadId, refreshKey }) => {
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: '', content: '', priority: 'medium', follow_up_date: '' });

  const canAddNote = ['super_admin', 'admin', 'branch_manager', 'team_leader', 'sales_executive', 'telecaller', 'dsa_partner', 'operations_executive'].includes(user?.role);

  const fetchNotes = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/leads/${leadId}/notes`);
      setNotes(res.data || []);
    } catch (e) {
      console.warn('[NotesPanel] failed to fetch notes:', e?.response?.status || e?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (leadId) fetchNotes();
  }, [leadId, refreshKey]);

  const handleSave = async () => {
    if (!form.title.trim() || !form.content.trim()) {
      showError('Title and remarks are required');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/leads/${leadId}/notes`, {
        title: form.title.trim(),
        content: form.content.trim(),
        priority: form.priority,
        follow_up_date: form.follow_up_date || null,
      });
      showSuccess('Note added');
      setForm({ title: '', content: '', priority: 'medium', follow_up_date: '' });
      setShowModal(false);
      fetchNotes();
    } catch (e) {
      showError(e.response?.data?.detail || 'Failed to save note');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (noteId) => {
    if (!window.confirm('Soft-delete this note? It will be hidden but kept for audit history.')) return;
    try {
      await api.delete(`/notes/${noteId}`);
      showSuccess('Note deleted');
      fetchNotes();
    } catch (e) {
      showError(e.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6" data-testid="lead-notes-panel">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
          <StickyNote size={20} /> Notes &amp; Remarks
          <span className="text-xs font-normal text-muted-foreground">({notes.length})</span>
        </h2>
        {canAddNote && (
          <button
            data-testid="add-note-button"
            onClick={() => setShowModal(true)}
            className="px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-sm hover:bg-primary/90 flex items-center gap-2"
          >
            <StickyNote size={14} /> Add Note
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-6">
          <Loader2 className="animate-spin text-primary" size={24} />
        </div>
      ) : notes.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-6">No notes yet. Click "Add Note" to get started.</p>
      ) : (
        <ol className="relative border-l border-border ml-3">
          {notes.map((n) => (
            <li key={n.id} className="mb-5 ml-6" data-testid={`note-${n.id}`}>
              <span className="absolute -left-1.5 mt-1.5 w-3 h-3 bg-primary/80 rounded-full ring-4 ring-background" />
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <h4 className="font-medium text-foreground">{n.title}</h4>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${PRIORITY_STYLES[n.priority] || PRIORITY_STYLES.medium}`}>
                      {n.priority?.toUpperCase()}
                    </span>
                    {n.follow_up_date && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] bg-violet-100 text-violet-700 inline-flex items-center gap-1">
                        <Clock size={10} /> {new Date(n.follow_up_date).toLocaleDateString('en-IN', { dateStyle: 'medium' })}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-foreground/85 whitespace-pre-wrap">{n.content}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    <span className="text-foreground/80 font-medium">{n.actor_name || 'User'}</span>
                    {n.actor_role && <span> · {formatRole(n.actor_role)}</span>}
                    {n.actor_employee_id && <span className="ml-1 font-mono">#{n.actor_employee_id}</span>}
                    {n.branch_name && <span> · {n.branch_name}</span>}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground whitespace-nowrap">{formatTime(n.created_at)}</span>
                  {(n.created_by === user?.id || ['super_admin', 'admin', 'branch_manager'].includes(user?.role)) && (
                    <button
                      data-testid={`delete-note-${n.id}`}
                      onClick={() => handleDelete(n.id)}
                      className="p-1 rounded hover:bg-destructive/10"
                      title="Soft-delete"
                    >
                      <Trash2 size={14} className="text-destructive" />
                    </button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ol>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" data-testid="add-note-modal">
          <div className="bg-card border border-border rounded-xl shadow-xl max-w-lg w-full">
            <div className="flex items-center justify-between p-5 border-b border-border">
              <h3 className="text-lg font-semibold text-foreground">Add Note / Remark</h3>
              <button onClick={() => setShowModal(false)} className="p-1 rounded hover:bg-muted">
                <X size={18} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Note Title *</label>
                <input
                  data-testid="note-title-input"
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                  placeholder="e.g., Initial Call - Customer interested"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Remarks / Description *</label>
                <textarea
                  data-testid="note-content-input"
                  rows="4"
                  value={form.content}
                  onChange={(e) => setForm({ ...form, content: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                  placeholder="Detailed remarks…"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Priority</label>
                  <select
                    data-testid="note-priority-select"
                    value={form.priority}
                    onChange={(e) => setForm({ ...form, priority: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Follow-up Date (Optional)</label>
                  <input
                    data-testid="note-followup-input"
                    type="date"
                    value={form.follow_up_date}
                    onChange={(e) => setForm({ ...form, follow_up_date: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background"
                  />
                </div>
              </div>
            </div>
            <div className="p-5 border-t border-border flex justify-end gap-2">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 border border-border rounded-lg hover:bg-muted">
                Cancel
              </button>
              <button
                data-testid="save-note-button"
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50"
              >
                {saving && <Loader2 className="animate-spin" size={16} />}
                Save Note
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotesPanel;
